import { createContext, createElement, useContext, useRef } from 'react';
import { atom, isShallowEqual } from '@reatom/framework';
// useIsomorphicEffect is useLayoutEffect in the browser and useEffect where there is no document,
// so a server render does not warn about an effect that cannot run there.
import { useAtom, useCtx, useIsomorphicEffect } from '@reatom/npm-react';
import { isObservable } from '@/js/libs/observable/index.ts';
// Unreachable default of the React context: a missing instance throws before the plain path reads it.
const NEVER_VALUE = {};
/**
 * Creates React contexts enhanced with a reatom-backed selector API.
 *
 * For every `Name(parameter) { ...hooks... }` entry the factory returns a `useNameContext`
 * hook (with `useNameContext.Provider` attached) supporting three read modes:
 * - `useNameContext()` — the whole state via plain React context; re-renders on any state change.
 * - `useNameContext('key')` — one field; re-renders only when that field changes.
 * - `useNameContext((state) => selected)` — a derived value; re-renders only when it changes.
 *
 * Selected values that are `Observable`s are unwrapped: the consumer receives the current
 * observable state and re-renders when it changes.
 *
 * All three modes read the state of the Provider's current render, so they agree within any commit.
 * The selector modes take only their re-render signal from a reatom atom owned by each mounted
 * Provider instance, which is what lets them skip consumers whose selection is unchanged.
 *
 * Known limitation: the Provider records its state during render, and React does not roll a ref
 * back when it throws a render away. A Provider under a Suspense boundary whose render is discarded
 * (a suspended child, an interrupted transition) therefore leaves a state behind that never
 * committed, and a selector consumer re-rendering before the next commit can read it while the plain
 * path still serves the committed one. The discarded state also keeps the subscription's state gate
 * closed, so Observable-driven updates stop waking selector consumers until the Provider's next
 * commit syncs the atom and releases it. Reading the committed state instead would cost a consumer
 * mounting in the Provider's own pass its first render, which is the trade-off taken here.
 */
// @ts-expect-error generic mismatch
export const createContexts = (contextsHook) => {
    const result = {};
    for (const [name, useContextLogic] of Object.entries(contextsHook)) {
        const hookName = `use${name}Context`;
        const providerName = `${name}Provider`;
        const context = createContext(NEVER_VALUE);
        const instanceContext = createContext(null);
        // Keeps the atoms of simultaneously mounted Provider instances apart in reatom devtools and logs.
        let instanceCount = 0;
        // Same, for the baseline atom every selector consumer of this context owns.
        let consumerCount = 0;
        const Provider = ({ children, value: parameter }) => {
            const state = useContextLogic(parameter);
            const ctx = useCtx();
            // Shallow stabilization: keeps the previous state object while every field is
            // reference-equal, so renders that change nothing move neither the context value nor the
            // atom. Unlike a deps array it holds for any set and order of the factory's keys.
            const stateRef = useRef(state);
            if (!isShallowEqual(stateRef.current, state)) {
                stateRef.current = state;
            }
            const value = stateRef.current;
            // Each Provider instance owns its state holder and notification atom, so several instances of
            // the same context (nested or in parallel subtrees) never share state. A ref (not useMemo)
            // keeps them stable across Fast Refresh re-renders.
            const instanceRef = useRef(null);
            instanceRef.current ??= { stateRef, notifyAtom: atom(value, `${name}Context#${++instanceCount}`) };
            const instance = instanceRef.current;
            // Syncs the atom after the commit. The atom is an external store for useSyncExternalStore
            // consumers, so it must not be written during render: React restarts rendering of the
            // subscribers, and when the state changes identity on every render pass (Fast Refresh
            // invalidates the factory's useMemo caches) that write-restart cycle becomes an infinite
            // render loop. Waiting for the commit costs no consistency, because the read paths take
            // their value from `stateRef`.
            useIsomorphicEffect(() => {
                if (!isShallowEqual(ctx.get(instance.notifyAtom), value)) {
                    instance.notifyAtom(ctx, value);
                }
            }, [ctx, instance, value]);
            // instanceContext delivers the state holder to the selector read paths; context re-renders
            // the plain path's consumers.
            return createElement(instanceContext.Provider, { value: instance }, createElement(context.Provider, { value, children }));
        };
        Provider.displayName = providerName;
        result[hookName] = Object.assign(
        // Hooks below are called conditionally, which stays within the rules of hooks only
        // because a given call site always passes the same kind of `selector` argument.
        (selector) => {
            const instance = useContext(instanceContext);
            if (!instance) {
                throw new Error(`${hookName} must be used within a ${providerName}`);
            }
            if (selector !== undefined) {
                const ctx = useCtx();
                const select = (state) => typeof selector === 'function' ? selector(state) : state[selector];
                // What this consumer last rendered — the baseline the subscription compares against.
                // A `selector` may close over props, so the subscription cannot compare against its own
                // previous result: that result was computed with an older closure. Held in an atom rather
                // than a ref because writing it after the commit re-runs the subscription, which is what
                // moves the tracking over when the selection switches to another Observable.
                const renderedAtomRef = useRef(null);
                renderedAtomRef.current ??= atom({ state: NEVER_VALUE, source: NEVER_VALUE, selected: NEVER_VALUE }, `${name}.useAtom._rendered#${++consumerCount}`);
                const renderedAtom = renderedAtomRef.current;
                // Subscription only: bumps a counter — and with it re-renders this consumer — when the
                // committed state no longer agrees with what the consumer rendered. The computed value is
                // ignored; the read below serves the state of the Provider's current render.
                useAtom((spyCtx, previous = 0) => {
                    const rendered = spyCtx.spy(renderedAtom);
                    const state = spyCtx.spy(instance.notifyAtom);
                    const raw = select(state);
                    const value = isObservable(raw) ? spyCtx.spy(raw.__INTERNAL_DO_NOT_USE_ATOM__) : raw;
                    // The Provider syncs the atom in its own effect, which React runs after the effects of
                    // its children. Until then this atom still holds the previous state while the consumer
                    // has already rendered the new one, and everything derived here would be compared
                    // against a newer baseline. Waiting costs nothing: that sync recomputes this atom.
                    if (!Object.is(state, instance.stateRef.current)) {
                        return previous;
                    }
                    // An Observable delivers its own updates independently of the context state, so its
                    // unwrapped value is the whole comparison. A plain selection can only start to
                    // disagree when the state changes: one that moved because the consumer's own props
                    // changed was already rendered by that very re-render. Gating on the state identity
                    // also bounds this to one bump per state, so a selector deriving a fresh value on
                    // every call cannot keep re-triggering itself.
                    const changed = isObservable(raw)
                        ? !Object.is(value, rendered.selected)
                        : !Object.is(state, rendered.state) && !Object.is(value, rendered.selected);
                    return changed ? previous + 1 : previous;
                }, 
                // The deps pin only the atom's identity. @reatom/npm-react re-reads the closure on
                // every render (`ref.anAtom = anAtom`), so each recomputation evaluates `select` from
                // the consumer's latest render — the "collide"/"shifting rows" tests guard exactly this.
                [instance, renderedAtom], { name: `${name}.useAtom._selector` });
                // The value comes from the Provider's current render, so every read mode returns the same
                // state — including in a consumer that mounts or re-renders in the Provider's own pass,
                // before the atom is synced. A selector may therefore close over props and state freely.
                const selected = select(instance.stateRef.current);
                const value = isObservable(selected) ? ctx.get(selected.__INTERNAL_DO_NOT_USE_ATOM__) : selected;
                // Records the baseline after the commit. Writing it during render would restart the
                // subscribers' render — the very loop this module is built to avoid.
                // `selected` is recorded next to its unwrapped value because a selection that switched to
                // another Observable holding an equal value changes nothing else here, and without that
                // write the subscription would keep spying the Observable it saw first.
                // Shallow comparison here only skips writes that would record an equivalent baseline: a
                // selector deriving a fresh value on every call would otherwise open a reatom transaction
                // on every render of every consumer. What counts as a change for the consumer is still
                // decided by identity, in the subscription above.
                useIsomorphicEffect(() => {
                    const rendered = ctx.get(renderedAtom);
                    if (!Object.is(rendered.state, instance.stateRef.current) ||
                        !isShallowEqual(rendered.source, selected) ||
                        !isShallowEqual(rendered.selected, value)) {
                        renderedAtom(ctx, { state: instance.stateRef.current, source: selected, selected: value });
                    }
                }, [ctx, instance, renderedAtom, selected, value]);
                return value;
            }
            return useContext(context);
        }, {
            Provider,
        });
    }
    return result;
};
